import {
  getGetCampaignApiV1QueryKey,
  useGetCampaignApiV1Suspense,
  useJoinCampaignApiV1,
} from "@/api/__generated__/api.schema";
import { CampaignUserRegisterStatus, CampaignUserUploadStatus } from "@/api/__generated__/models";
import { KnownError } from "@/api/client";
import { PageContainer } from "@/components/common/page-container";
import { Preload } from "@/components/preload";
import { routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { useCampaignId } from "@/routes/campaigns/hooks/use-campaign-id";
import { useCurrentCampaignInfo } from "@/routes/campaigns/hooks/use-current-campaign-info";
import { calculateRemainingDays } from "@/utils/date";
import { getSnsLabel } from "@/utils/sns";
import {
  Button,
  List,
  ListItem,
  Separator,
  ShadowBox,
  Thumbnail,
  ThumbnailImage,
  Typography,
  cn,
  getAssetsUrl,
  useConfirm,
  useIsMobile,
  useModal,
} from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

export function CampaignViewPage() {
  return (
    <>
      <Preload.Image fileNames={[getAssetsUrl("sefie.webp")]} />
      <PageContainer
        nav={
          <PageContainer.Navigation
            title="Campaigns"
            links={[
              { to: routes.CAMPAIGNS.root, text: "Discover" },
              { to: routes.CAMPAIGNS.my, text: "My campaigns" },
            ]}
          />
        }
      >
        <HeroMobile className="block desktop:hidden m-[-20px] mb-6" />
        <HeroDesktop className="hidden desktop:block mb-6" />
        <div className="flex gap-6">
          <Contents className="w-full" />
          <CampaignButton className="hidden desktop:flex max-w-[320px] sticky top-6 self-start h-fit" />
        </div>
      </PageContainer>
      <CampaignButton className="flex desktop:hidden fixed bg-white py-3 px-4 bottom-0 self-start h-fit border border-t-borderLight" />
    </>
  );
}

function Contents({ className }: { className?: string }) {
  const info = useCurrentCampaignInfo();
  const isMobile = useIsMobile();

  return (
    <ShadowBox className={cn("p-6 space-y-6 mb-12", className)}>
      <Typography size={isMobile ? "xl" : "2xl"} className="mb-6" weight={600}>
        {info.title}
      </Typography>

      <section className="flex flex-col gap-[10px]">
        <div className="flex items-center gap-2 text-textMuted">
          <i className="i-bp-info inline-block w-4 h-4 flex-shrink-0" />
          <Typography size="xs" weight={500}>
            {info.trackName} - {info.artistName}
          </Typography>
        </div>
        <div className="flex items-center gap-2 text-textMuted">
          <i className="i-bp-calendar inline-block w-4 h-4 flex-shrink-0" />
          <Typography size="xs" weight={500}>
            {calculateRemainingDays(info.campaignUploadEndDate)}
          </Typography>
        </div>
        <div className="flex items-center gap-2 text-textMuted">
          <i className="i-bp-sidebar-video inline-block w-4 h-4 flex-shrink-0" />
          <Typography size="xs" weight={500}>
            {getSnsLabel(info.meta?.platform)}
          </Typography>
        </div>
      </section>

      <Separator />

      <section className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-sidebar-announce inline-block size-6 flex-shrink-0" />
          <Typography size="lg" weight={600}>
            About campaign
          </Typography>
        </div>
        <Typography size="paragraphXs" color="textSecondary" className="whitespace-pre-wrap">
          {info.meta?.about}
        </Typography>
      </section>

      <Separator />

      <section className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-sidebar-video inline-block size-6 flex-shrink-0" />
          <Typography size="lg" weight={600}>
            Platform
          </Typography>
        </div>
        <Typography size="xs" color="textSecondary" className="flex items-center gap-[6px]" weight={500}>
          <Thumbnail className="size-6">
            <ThumbnailImage src={getAssetsUrl("sns/tiktok.png")} />
          </Thumbnail>
          {getSnsLabel(info.meta?.platform)}
        </Typography>
      </section>

      <Separator />

      <section className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-info inline-block size-6 flex-shrink-0" />
          <Typography size="lg" weight={600}>
            Guidelines
          </Typography>
        </div>

        <Typography size="paragraphXs" color="textSecondary">
          <strong>Step 1:</strong> Submit the Sign-up form to confirm your participation in this campaign.
          <br />
          <strong>Step 2:</strong> Choose between two formats and create a 10 - 20 second video using Hanz "Best Friend"
          <div className="pl-4">• Using the Hanz "Best Friend" Offical TikTok Audio</div>
          <strong>Step 3:</strong> Include hashtag #Bestfriend #Hanz
          <br />
          <strong>Step 4:</strong> Upload your video on TikTok during this period, May 27th, 2025 - June 6th, 11:59 PM
          (PT)
        </Typography>
      </section>

      <Separator />

      <section className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-check inline-block size-6 flex-shrink-0" />
          <Typography size="lg" weight={600}>
            What You'll Do
          </Typography>
        </div>
        <div>
          <Typography size="paragraphXs" color="textSecondary" className="leading-[1.8]">
            <strong className="block mb-6">Choose between two flexible formats:</strong>
            <Typography size="paragraphXs" color="textSecondary" className="leading-[1.8] mb-6">
              <strong>🎬 Version 1: "What I Found"</strong>
              <List>
                <ListItem>
                  <strong>Start with:</strong> "What I found in ____"
                </ListItem>
                <div className="pl-4">
                  <ul className="list-disc pl-4">
                    <li>"What I found in College..." / "What I found in high school..." ...My Bestfriend!</li>
                  </ul>
                </div>
                <ListItem className="pl-4">
                  <strong>Emotional reveal:</strong> At the lyric drop "Oh my best friend", show a meaningful photo or
                  video of your pet, friend, or family member.
                </ListItem>
              </List>
            </Typography>

            <Typography size="paragraphXs" color="textSecondary" className="leading-[1.8]">
              <strong>🕰️ Version 2: "Then & Now"</strong>
              <List>
                <ListItem>
                  <strong>Start with a present-day scene:</strong> e.g., "We're adults now, busy and far apart…", "He's
                  old now and can't run anymore…", "Mom and Dad don't talk as much these days…"
                </ListItem>
                <ListItem>
                  <strong>Emotional transition:</strong> At the lyric "Oh my best friend," shift to nostalgic clips or
                  photos highlighting deep memories (childhood friends, joyful pets, family moments).
                </ListItem>
              </List>
            </Typography>
          </Typography>
        </div>
      </section>

      <Separator />

      {info.meta?.sampleVideos != null ? (
        <section className="grid grid-cols-1">
          <div className="flex items-center gap-[10px] mb-4">
            <i className="i-bp-channel-bold inline-block size-6 flex-shrink-0 text-textMuted" />
            <Typography size="lg" weight={600}>
              Video Samples
            </Typography>
          </div>
          <div
            className={cn("grid grid-cols-1 gap-3", {
              "desktop:grid-cols-1": info.meta?.sampleVideos?.length === 1,
              "desktop:grid-cols-2": info.meta?.sampleVideos?.length === 2,
            })}
          >
            {info.meta?.sampleVideos?.map((video) => (
              <div key={video.url} className="space-y-3">
                <Typography size="paragraphXs" color="textSecondary" weight={700}>
                  {video.title}
                </Typography>
                <video src={video.url} controls>
                  <track kind="captions" src="" label="English" />
                </video>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {info.meta?.musicVideoUrl != null ? (
        <section className="grid grid-cols-1">
          <div className="flex items-center gap-[10px] mb-4">
            <i className="i-bp-channel-bold inline-block size-6 flex-shrink-0 text-textMuted" />
            <Typography size="lg" weight={600}>
              Listen to the Track
            </Typography>
          </div>
          <div className="aspect-video w-full mb-3 rounded-[16px] overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              src={info.meta?.musicVideoUrl}
              title="Benson Boone - Mystical Magical"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      ) : null}

      <Separator />

      <section className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-star inline-block size-6 flex-shrink-0 text-textMuted" />
          <Typography size="lg" weight={600}>
            Caption Examples
          </Typography>
        </div>
        <Typography size="paragraphXs" color="textSecondary" className="whitespace-pre-wrap">
          {info.meta?.guideLine}
        </Typography>
        <Typography size="paragraphXs" color="textSecondary" className="whitespace-pre-wrap" weight={700}>
          Feel free to adapt these examples or create your own sentences that perfectly fit your bestie and story!
        </Typography>
      </section>

      <Separator />

      <section className="grid grid-cols-1">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-calendar inline-block size-6 flex-shrink-0 text-textMuted" />
          <Typography size="lg" weight={600}>
            Timelines
          </Typography>
        </div>
        <div className="relative space-y-3">
          <div className="absolute w-[1px] bg-borderLight h-[90px] left-[3.5px] top-4 z-0" />
          <div className="flex items-center gap-2">
            <div className="size-2 bg-brandTangerine rounded-full z-10" />
            <Typography size="xs" color="textMuted" className="flex items-center justify-between w-full">
              Registration period
              <Typography weight={500} color="textPrimary">
                {Temporal.PlainDate.from(info.campaignSignUpStartDate).toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                })}{" "}
                -{" "}
                {Temporal.PlainDate.from(info.campaignSignUpEndDate)
                  .toZonedDateTime({ timeZone: "America/Los_Angeles", plainTime: "23:59" })
                  .toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                    timeZoneName: "short",
                  })}
              </Typography>
            </Typography>
          </div>
          {info.meta?.musicReleaseDateTime != null ? (
            <div className="flex items-center gap-2">
              <div className="size-2 bg-brandTangerine rounded-full z-10" />
              <Typography size="xs" color="textMuted" className="flex items-center justify-between w-full">
                Music release
                <Typography weight={500} color="textPrimary">
                  {Temporal.PlainDateTime.from(info.meta?.musicReleaseDateTime).toLocaleString("en-US", {
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                    timeZoneName: "long",
                  })}
                </Typography>
              </Typography>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <div className="size-2 bg-brandTangerine rounded-full z-10" />
            <Typography size="xs" color="textMuted" className="flex items-center justify-between w-full">
              Posting period
              <Typography weight={500} color="textPrimary">
                {Temporal.PlainDate.from(info.campaignUploadStartDate).toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                })}{" "}
                -{" "}
                {Temporal.PlainDate.from(info.campaignUploadEndDate)
                  .toZonedDateTime({ timeZone: "America/Los_Angeles", plainTime: "23:59" })
                  .toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                    timeZoneName: "short",
                  })}
              </Typography>
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-2 bg-brandTangerine rounded-full z-10" />
            <Typography size="xs" color="textMuted" className="flex items-center justify-between w-full">
              Payment period
              <Typography weight={500} color="textPrimary">
                {Temporal.PlainDate.from(info.campaignRevenueConfirmationDate).toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                })}
              </Typography>
            </Typography>
          </div>
        </div>
      </section>

      <Separator />

      <section className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-[10px]">
          <i className="i-bp-info inline-block size-6 flex-shrink-0 text-textMuted" />
          <Typography size="lg" weight={600}>
            Payouts
          </Typography>
        </div>
        <div>
          <Typography size="paragraphXs" color="textSecondary" asChild>
            <List>
              <ListItem>
                All payouts are handled directly through our website. By creating a BackPac account, you can securely
                provide your payment details, track your rewards, and easily participate in future campaigns.
              </ListItem>
              <ListItem>
                <strong>How Does the Payout Process Work?</strong>
              </ListItem>
            </List>
          </Typography>
          <Typography size="paragraphXs" color="textSecondary" asChild>
            <ol className="list-[lower-alpha] list-inside pl-6">
              <li>Submit your video URL.</li>
              <li>We'll add your reward to your BackPac account balance.</li>
              <li>Provide your payout method and tax information in your BackPac account.</li>
              <li>
                Request your withdrawal! (Once requested, it typically takes 8–15 days for funds to reach your account).
              </li>
            </ol>
          </Typography>
        </div>
      </section>
    </ShadowBox>
  );
}

function HeroMobile({ className }: { className?: string }) {
  const info = useCurrentCampaignInfo();

  return (
    <div className={cn("block desktop:hidden relative h-[160px] overflow-hidden", className)}>
      <img src={info.meta?.material?.cover} alt="Mystical Magical" />
    </div>
  );
}

function HeroDesktop({ className }: { className?: string }) {
  const info = useCurrentCampaignInfo();

  return (
    <div className={cn("relative h-[300px] overflow-hidden rounded-[16px]", className)}>
      <div className="absolute inset-0 bg-black opacity-10" />
      <img
        src={info.meta?.material?.cover}
        alt=""
        className="absolute object-cover blur-xl brightness-10  -translate-y-[30%]"
      />
      <img
        src={info.meta?.material?.cover}
        alt=""
        className="block mx-auto h-full absolute left-1/2 -translate-x-1/2"
      />
    </div>
  );
}

function CampaignButton({ className }: { className?: string }) {
  const campaignId = useCampaignId();
  const workspaceId = useWorkspaceId();
  const naviagte = useWorkspaceNavigate();
  const {
    data: {
      data: { item },
    },
  } = useGetCampaignApiV1Suspense(workspaceId, campaignId);

  if (
    item.userRegisterStatus === CampaignUserRegisterStatus.NOT_REGISTERED ||
    item.userUploadStatus === CampaignUserUploadStatus.UPCOMING
  ) {
    return (
      <div className={cn("flex flex-col items-center gap-3 w-full", className)}>
        <JoinCampaignButton disabled={item.userRegisterStatus === CampaignUserRegisterStatus.REGISTERED} />
        {item.userRegisterStatus === CampaignUserRegisterStatus.REGISTERED ? null : (
          <Typography size="paragraphXs" className="text-center">
            Be part of the first wave — join now and start creating
          </Typography>
        )}
      </div>
    );
  }
  if (item.userUploadStatus === CampaignUserUploadStatus.AVAILABLE) {
    return (
      <div className={cn("flex flex-col items-center gap-3 w-full", className)}>
        <Button className={cn("w-full")} size="lg" onClick={() => naviagte(routes.CAMPAIGNS._join(campaignId))}>
          Submit URL
        </Button>
        <Typography size="paragraphXs" className="text-center">
          Don't forget — submitting your video link is required to get your reward.
        </Typography>
      </div>
    );
  }

  if (item.userUploadStatus === CampaignUserUploadStatus.SUBMITTED) {
    return (
      <div className={cn("flex flex-col items-center gap-3 w-full", className)}>
        <Button disabled={true} size="lg" className="w-full">
          Submitted
        </Button>
      </div>
    );
  }
}

function JoinCampaignButton({ className, disabled }: { className?: string; disabled?: boolean }) {
  const campaignId = useCampaignId();
  const workspaceId = useWorkspaceId();
  const naviagte = useWorkspaceNavigate();
  const queryClient = useQueryClient();
  const { confirm: answerRegisterChannel } = useConfirm();
  const info = useCurrentCampaignInfo();
  const { confirm } = useConfirmJoin();
  const { mutate: joinCampaign, isPending } = useJoinCampaignApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCampaignApiV1QueryKey(workspaceId, campaignId) });
      },
      onError: async (error) => {
        if (KnownError.is(error) && error.message === "NEED_TO_REGISTER_CHANNEL") {
          const confirmed = await answerRegisterChannel({
            title: "Register channel",
            description: "You have to register a channel first to join this campaign.",
            confirmText: "Go to register",
            cancelText: "Cancel",
          });

          if (confirmed) {
            naviagte(routes.CHANNELS.root);
          }
        }
      },
    },
  });

  return (
    <Button
      className={cn("w-full", className)}
      size="lg"
      disabled={disabled}
      loading={isPending}
      onClick={async () => {
        const platform = info.meta?.platform; // Probably, in the future, we should support multiple platforms.

        if (platform == null) {
          throw new Error("Platform is not set");
        }

        const isConfirmed = await confirm();

        if (isConfirmed) {
          joinCampaign({
            workspaceId,
            campaignId,
            data: {
              uploadDate: info.campaignUploadStartDate,
              platform,
            },
          });
        }
      }}
    >
      Join campaign
    </Button>
  );
}

function useConfirmJoin() {
  const { open, close } = useModal();

  return {
    confirm: () => {
      return new Promise((resolve) => {
        open({
          title: "Join campaign",
          onClose: () => resolve(false),
          className: "max-w-[320px]",
          children: (
            <div className="flex flex-col items-center gap-4 text-center">
              <img src={getAssetsUrl("sefie.webp")} alt="" className="w-full" />
              <div className="space-y-2">
                <Typography font="unbounded" size="md">
                  You're In!
                </Typography>
                <Typography size="paragraphXs" color="textMuted">
                  You've joined the campaign — now it's time to create your video and submit the link once it's live.
                </Typography>
              </div>
              <Button
                onClick={() => {
                  resolve(true);
                  close();
                }}
              >
                Got it
              </Button>
            </div>
          ),
        });
      });
    },
  };
}
